<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import WorkspaceSettingsLayout from '@/modules/workspace/components/workspace-settings-layout.vue'
import WorkspaceNav from '@/modules/workspace/components/workspace-nav.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { RouterLinkBack, VButton, VButtonInvisible, VIcon, VModalDialog } from '@/modules/shared/components'

import { useModal } from '@/modules/shared/composables/use-modal'

const workspaceSettingsStore = useWorkspaceSettingsStore()
const authStore = useAuthStore()

const selectedTab = computed(() => 'delete_account' as const)

// Delete account functionality
const deleteModal = useModal()
const deletePassword = ref('')
const deleteError = ref('')
const isDeleting = ref(false)

const openDeleteModal = () => {
  deletePassword.value = ''
  deleteError.value = ''
  deleteModal.open()
}

const confirmDelete = async () => {
  if (!deletePassword.value) {
    deleteError.value = 'Password is required'
    return
  }

  isDeleting.value = true
  deleteError.value = ''

  // Capture the group name *before* deletion so we can personalize the goodbye page.
  const groupName = workspaceSettingsStore.workspace?.name || ''
  const result = await workspaceSettingsStore.deleteWorkspace(deletePassword.value)

  if (result.success) {
    await authStore.logout()
    const query = groupName ? `?group=${encodeURIComponent(groupName)}` : ''
    window.location.href = `/goodbye${query}`
  } else {
    deleteError.value = result.error || 'Failed to delete account'
    isDeleting.value = false
  }
}

onMounted(() => {
  workspaceSettingsStore.fetch()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <WorkspaceNav />

    <WorkspaceSettingsLayout :selected-tab="selectedTab">
      <div class="space-y-6">
        <div class="text-lg font-semibold">Delete Account</div>

        <div class="rounded-lg border border-red-100 bg-red-50/50 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <div class="text-sm font-medium text-red-700">Permanently Delete Account</div>
              <p class="mt-1 text-sm text-red-600/70">
                Permanently delete this group and all associated data. This cannot be undone.
              </p>
            </div>
            <button
              type="button"
              @click="openDeleteModal"
              class="flex shrink-0 items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            >
              <VIcon name="Trash" class="h-4 w-4 stroke-red-500" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </WorkspaceSettingsLayout>

    <!-- Delete Confirmation Modal -->
    <VModalDialog :modalStore="deleteModal">
      <template #main>
        <div class="text-center">
          <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <VIcon name="Trash" class="h-6 w-6 stroke-red-600" />
          </div>
          <h3 class="mb-2 text-lg font-semibold text-gray-900">Confirm Account Deletion</h3>
          <p class="mb-4 text-sm text-gray-600">
            Are you sure you want to delete your account? This will permanently delete all data including companies,
            investors, and files.
          </p>
          <div class="text-left">
            <label for="delete-password" class="mb-1 block text-sm font-medium text-gray-700">
              Enter your password to confirm
            </label>
            <input
              id="delete-password"
              v-model="deletePassword"
              type="password"
              placeholder="Your password"
              class="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50"
              @keyup.enter="confirmDelete"
            />
            <p v-if="deleteError" class="mt-2 text-sm text-red-600">{{ deleteError }}</p>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-3">
          <VButton variant="v-white" :click="deleteModal.close" :disabled="isDeleting"> Cancel </VButton>
          <VButton variant="v-red" :click="confirmDelete" :loading="isDeleting"> Delete Account </VButton>
        </div>
      </template>
    </VModalDialog>
  </TheLayout>
</template>
